import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { apiGet, apiPost, UNAUTHORIZED_EVENT } from "@/lib/api";
import { clearMemberToken, getMemberToken, setMemberToken } from "@/lib/auth";
import {
  clearMemberToken as appClearMemberToken,
  setMemberToken as appSetMemberToken,
} from "@/lib/mdlApp";
import type { ClubFull, LoginMember, LoginResult } from "@/lib/types";
import { useToast } from "@/components/feature/ToastProvider";

interface AuthContextValue {
  member: LoginMember | null;
  ready: boolean;
  loginOpen: boolean;
  loginMode: string;
  openLogin: () => void;
  closeLogin: () => void;
  login: (riNo: string, birth4?: string) => Promise<void>;
  logout: () => Promise<void>;
  requireLogin: (redirectPath?: string) => boolean;
}

const REDIRECT_KEY = "mdl_login_redirect";

const AuthContext = createContext<AuthContextValue | null>(null);

function extractMember(data: unknown): LoginMember | null {
  if (!data || typeof data !== "object") return null;
  const rec = data as Record<string, unknown>;
  if (rec.member && typeof rec.member === "object") {
    return rec.member as LoginMember;
  }
  if (typeof rec.no === "number" || typeof rec.name === "string") {
    return rec as unknown as LoginMember;
  }
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const [member, setMember] = useState<LoginMember | null>(null);
  const [ready, setReady] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginMode, setLoginMode] = useState<string>("ri");
  const modeLoaded = useRef(false);

  // 로그인 방식(ri / ri_birth)을 club.php 에서 1회 조회
  const loadLoginMode = useCallback(async () => {
    if (modeLoaded.current) return;
    modeLoaded.current = true;
    try {
      const res = await apiGet<ClubFull>("club.php");
      setLoginMode(res.data?.login_mode || "ri");
    } catch {
      setLoginMode("ri");
    }
  }, []);

  // 앱 시작 시 로그인 상태 확인 (me.php)
  useEffect(() => {
    let active = true;
    const token = getMemberToken();
    if (!token) {
      setReady(true);
      return () => {
        active = false;
      };
    }
    apiGet<LoginMember>("me.php")
      .then((res) => {
        if (!active) return;
        const loginFlag = (res as unknown as { login?: number }).login;
        if (loginFlag === 0) {
          setMember(null);
          return;
        }
        setMember(extractMember(res.data));
      })
      .catch(() => {
        if (!active) return;
        clearMemberToken();
        setMember(null);
      })
      .finally(() => {
        if (active) setReady(true);
      });
    return () => {
      active = false;
    };
  }, []);

  // 401(세션 만료) 수신 시 토큰 정리 + 로그인 모달
  useEffect(() => {
    const onUnauthorized = () => {
      clearMemberToken();
      appClearMemberToken();
      setMember(null);
      setLoginOpen(true);
    };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const openLogin = useCallback(() => {
    setLoginOpen(true);
    void loadLoginMode();
  }, [loadLoginMode]);

  const closeLogin = useCallback(() => setLoginOpen(false), []);

  const login = useCallback(async (riNo: string, birth4?: string) => {
    const res = await apiPost<LoginResult>("login.php", {
      ri_no: riNo,
      birth4,
    });
    const data = res.data as LoginResult;
    const token = data?.token || "";
    if (!token) throw new Error("로그인 정보를 받지 못했습니다.");
    setMemberToken(token);
    // 앱(WebView)이 백그라운드에서 걸음을 올릴 수 있게 회원 토큰을 앱에 보관시킨다.
    // 브라우저에서는 MdlApp 이 없어 아무 일도 일어나지 않는다.
    appSetMemberToken(token);
    setMember(data.member ?? null);
    setLoginOpen(false);

    // 로그인 전에 요청된 이동 경로가 있으면 그곳으로 이동
    let redirect: string | null = null;
    try {
      redirect = sessionStorage.getItem(REDIRECT_KEY);
    } catch {
      redirect = null;
    }
    if (redirect) {
      try {
        sessionStorage.removeItem(REDIRECT_KEY);
      } catch {
        /* 무시 */
      }
      if (typeof window.REACT_APP_NAVIGATE === "function") {
        window.REACT_APP_NAVIGATE(redirect);
      } else {
        window.setTimeout(() => window.location.reload(), 300);
      }
    } else {
      // 헤더 반영을 위해 현재 페이지 새로고침
      window.setTimeout(() => window.location.reload(), 300);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiPost<unknown>("logout.php", {});
    } catch {
      /* 이미 만료된 토큰이어도 로컬은 정리 */
    }
    clearMemberToken();
    appClearMemberToken();
    setMember(null);
    toast("로그아웃되었습니다.", "info");
    window.setTimeout(() => window.location.reload(), 300);
  }, [toast]);

  const requireLogin = useCallback(
    (redirectPath?: string) => {
      if (member) return true;
      toast("로그인 후 이용 가능합니다.", "info");
      if (redirectPath) {
        try {
          sessionStorage.setItem(REDIRECT_KEY, redirectPath);
        } catch {
          /* 무시 */
        }
      }
      setLoginOpen(true);
      void loadLoginMode();
      return false;
    },
    [member, toast, loadLoginMode]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      member,
      ready,
      loginOpen,
      loginMode,
      openLogin,
      closeLogin,
      login,
      logout,
      requireLogin,
    }),
    [member, ready, loginOpen, loginMode, openLogin, closeLogin, login, logout, requireLogin]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth 는 AuthProvider 안에서만 사용할 수 있습니다.");
  return ctx;
}